import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Plus, Trash2, Save, X } from 'lucide-react';

const NavEditor = () => {
    const [navItems, setNavItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');

    // New/Edit Item State
    const [isEditing, setIsEditing] = useState(false);
    const [currentItem, setCurrentItem] = useState({ title: '', sectionId: '', order: 0 });

    const fetchNavItems = async () => {
        try {
            const { data } = await api.get('/nav');
            setNavItems(data);
            setLoading(false);
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNavItems();
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this nav item?')) return;
        try {
            await api.delete(`/nav/${id}`);
            fetchNavItems();
        } catch (error) {
            console.error(error);
        }
    };

    const handleEdit = (item) => {
        setCurrentItem(item);
        setIsEditing(true);
    };

    const handleAddNew = () => {
        setCurrentItem({ title: '', sectionId: '', order: navItems.length + 1 });
        setIsEditing(true);
    };

    const handleSaveItem = async (e) => {
        e.preventDefault();
        try {
            if (currentItem._id) {
                await api.put(`/nav/${currentItem._id}`, currentItem);
            } else {
                await api.post('/nav', currentItem);
            }
            setIsEditing(false);
            fetchNavItems();
            setMessage('Navigation saved successfully');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error(error);
            setMessage('Error saving navigation');
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div className="max-w-4xl mx-auto bg-white p-8 rounded shadow text-gray-900">
            <h2 className="text-2xl font-bold mb-6">Edit Navigation</h2>
            {message && <div className={`p-4 mb-4 rounded ${message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{message}</div>}

            {isEditing ? (
                <div className="bg-gray-50 p-6 rounded border mb-6">
                    <h3 className="font-bold mb-4">{currentItem._id ? 'Edit Item' : 'Add New Item'}</h3>
                    <form onSubmit={handleSaveItem} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium">Link Title</label>
                            <input
                                type="text"
                                value={currentItem.title}
                                onChange={(e) => setCurrentItem({ ...currentItem, title: e.target.value })}
                                required
                                className="w-full mt-1 p-2 border rounded"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium">Link Target (Section ID)</label>
                            <input
                                type="text"
                                value={currentItem.sectionId}
                                onChange={(e) => setCurrentItem({ ...currentItem, sectionId: e.target.value })}
                                required
                                className="w-full mt-1 p-2 border rounded"
                                placeholder="e.g. hero, contact, about"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium">Order</label>
                            <input
                                type="number"
                                value={currentItem.order}
                                onChange={(e) => setCurrentItem({ ...currentItem, order: Number(e.target.value) })}
                                className="w-full mt-1 p-2 border rounded"
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setIsEditing(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100">Cancel</button>
                            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Save Item</button>
                        </div>
                    </form>
                </div>
            ) : (
                <button onClick={handleAddNew} className="mb-6 flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" /> Add Link
                </button>
            )}

            <div className="border rounded divide-y">
                {navItems.map((item) => (
                    <div key={item._id} className="p-4 flex justify-between items-center hover:bg-gray-50">
                        <div>
                            <h4 className="font-medium">{item.title}</h4>
                            <p className="text-sm text-gray-500">#{item.sectionId} — Order: {item.order}</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => handleEdit(item)} className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                                Edit
                            </button>
                            <button onClick={() => handleDelete(item._id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
                {navItems.length === 0 && <p className="p-8 text-center text-gray-500">No navigation items found.</p>}
            </div>
        </div>
    );
};

export default NavEditor;
